import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import FieldWrapper from '../../fields/FieldWrapper/FieldWrapper'
import Input from '../../fields/Input/Input'
import css from './ReviewForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import { requiredMessage } from '../../../lib/form'
import Phone from '../../fields/Phone/Phone'
import Textarea from '../../fields/Textarea/Textarea'
import ToggleCheckBox from '../../fields/ToggleCheckBox/ToggleCheckBox'
import Button from '../../common/Button/Button'
import { ButtonTheme, IUploadedFile } from '../../types'
import RateNum from '../../fields/RateNum/RateNum'
import { useMutation } from '@apollo/client'
import { SEND_REVIEW } from '../../../queries/reviewMutation'
import {
  send_review,
  send_reviewVariables,
} from '../../../__generated__/send_review'
import Sended from '../Sended/Sended'
import LoadingIcon from '../../../public/static/icons/form-loader.svg'
import Captcha from '../Auth/Captcha'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { LayoutContext } from '../../../contexts/layoutProvider'
import FileUpload from '../../common/FileUpload'

const imageCount = 6
const videoCount = 3

interface IReviewForm {
  name: string
  email: string
  phone: string
  comment: string
  rate: string
  privacyPolicy: boolean
}
const ReviewForm: FC<{
  productId?: string
  onClose: () => void
}> = ({ productId, onClose }) => {
  const { errors, register, handleSubmit, control, reset, watch } =
    useForm<IReviewForm>({
      mode: 'all',
    })

  const { setCommonErrorMessage } = useContext(LayoutContext)
  const [sended, setSended] = useState(false)
  const [photos, setPhotos] = useState<IUploadedFile[]>([])
  const [videos, setVideos] = useState<IUploadedFile[]>([])
  const timeoutRef = useRef(null)

  const policy = watch('privacyPolicy')

  const { executeRecaptcha } = useGoogleReCaptcha()

  const [sendReview, { loading }] = useMutation<
    send_review,
    send_reviewVariables
  >(SEND_REVIEW)

  const submit = async (values: IReviewForm) => {
    try {
      setCommonErrorMessage('')
      const rate = +values.rate
      const id = productId ? productId : null
      const token = await executeRecaptcha()
      const res = await sendReview({
        variables: {
          ...values,
          grade: rate,
          productId: id,
          token,
          photos: photos,
          videos: videos,
        },
      })

      if (res?.data?.sendReview?.success) {
        setSended(true)
        setPhotos([])
        setVideos([])
        reset()

        timeoutRef.current = setTimeout(() => {
          onClose()
          setSended(false)
        }, 5000)
      } else {
        throw new Error('Ошибка при отправке отзыва')
      }
    } catch (error) {
      setCommonErrorMessage(
        'Возникла ошибка при отправки формы. Воспользуйтесь формой позже.',
      )
      console.error(error)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return sended ? (
    <div className={css.sended_wrapper}>
      <Sended
        heading="Cпасибо!"
        subheading="Ваш отзыв будет опубликован после прохождения модерации"
      />
    </div>
  ) : (
    <div className={css.content}>
      <h4 className={css.form_heading}>Ваш отзыв</h4>
      <form onSubmit={handleSubmit(submit)}>
        <FieldWrapper
          label="Имя *"
          errorMessage={errors?.name?.message}
          className={css.input}
        >
          <Input
            ref={register({
              required: requiredMessage,
              minLength: { value: 3, message: 'Минимум 3 символа' },
            })}
            required
            name="name"
            id="name"
            error={errors?.name}
          />
        </FieldWrapper>
        <FieldWrapper
          label="E-mail"
          errorMessage={errors?.email?.message}
          className={css.input}
        >
          <Input
            ref={register({
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Некорректная почта',
              },
            })}
            name="email"
            id="email"
            type="email"
            error={errors?.email}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Телефон"
          tooltip="для оперативной связи при наличии претензии"
          className={css.input}
        >
          <Controller
            name="phone"
            defaultValue=""
            control={control}
            render={({ onChange, onBlur }) => (
              <Phone onChange={onChange} onBlur={onBlur} id="phone" />
            )}
          />
        </FieldWrapper>
        <FieldWrapper
          label="Ваш отзыв *"
          errorMessage={errors?.comment?.message}
          className={css.input}
        >
          <Textarea
            name="comment"
            id="comment"
            register={register({
              required: requiredMessage,
              minLength: { value: 5, message: 'Минимум 5 символов' },
            })}
          />
        </FieldWrapper>

        <FileUpload
          count={imageCount}
          files={photos}
          setFiles={setPhotos}
          fileType="image"
          className={css.uploadInput}
        />

        <FileUpload
          count={videoCount}
          files={videos}
          setFiles={setVideos}
          fileType="video"
          className={css.uploadInput}
        />

        <RateNum
          name="rate"
          id="rate"
          register={register()}
          className={css.rate}
        />

        <FieldWrapper errorMessage={errors?.privacyPolicy?.message}>
          <ToggleCheckBox
            name="privacyPolicy"
            id="privacyPolicy"
            label={
              <>
                согласен{' '}
                <a href="/info/policy" target="_blank" className={css.policy}>
                  на&nbsp;обработку персональных данных
                </a>{' '}
                и&nbsp;{' '}
                <a
                  href="/info/personal-data-consent"
                  target="_blank"
                  className={css.policy}
                >
                  даю согласие
                </a>{' '}
                на&nbsp;их&nbsp;использование
              </>
            }
            ref={register({
              validate: (value) => {
                if (!value) {
                  return requiredMessage
                }
              },
            })}
          />
        </FieldWrapper>

        {loading ? (
          <div className={css.loading}>
            <LoadingIcon />
          </div>
        ) : (
          <Button
            disabled={!policy}
            type="submit"
            theme={ButtonTheme.black}
            className={css.submit}
          >
            оставить отзыв
          </Button>
        )}
      </form>
    </div>
  )
}

export default ReviewForm
